import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import VideoRecorderModal from '../../my_inbox/VideoRecorderModal/VideoRecorderModal';
import VideoPreviewModal from '../../my_inbox/VideoPreviewModal/VideoPreviewModal';
import ConfirmVideoDeleteModal from '../../my_inbox/MessageComposer/ConfirmVideoDeleteModal';
import { uploadVideoToCloudinary } from '../../views_shared_copies/api/videoUploads';
import {
  signPitchVideoUpload, getPitchVideo, attachPitchVideo, deletePitchVideo,
} from '../../views_shared_copies/api/pitchVideos';

// "Stand out by adding a video pitch!" — record-only by design, optional always. Recordings
// upload direct to Cloudinary and attach to the V2 pitch immediately; a reopened draft
// rediscovers its video via GET (the V1 pitch payload knows nothing about V2 attachments).
function PitchVideoSection({
  pitchId, opportunityDetails, onUploadingChange, onAttachedChange,
}) {
  const [phase, setPhase] = useState('empty'); // empty | uploading | attached
  const [attachment, setAttachment] = useState(null); // { id, posterUrl, videoUrl }
  const [localUrl, setLocalUrl] = useState(null); // blob URL for instant preview after recording
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showRecorder, setShowRecorder] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // The poster derivative is generated eager_async — right after upload it can 404 briefly, and a
  // broken <img> collapses to nothing. Fall back to the dark placeholder until a reopen.
  const [posterFailed, setPosterFailed] = useState(false);
  const localUrlRef = useRef(null);

  useEffect(() => () => {
    if (localUrlRef.current) { URL.revokeObjectURL(localUrlRef.current); }
  }, []);

  // The composer's submit gate follows this (a recorded video stands in for pitch text); phase is
  // the single source of truth, so every transition — rediscovery, attach, delete — reports through it.
  const onAttachedChangeRef = useRef(onAttachedChange);
  onAttachedChangeRef.current = onAttachedChange;
  useEffect(() => {
    onAttachedChangeRef.current?.(phase === 'attached');
  }, [phase]);

  useEffect(() => {
    if (!pitchId) { return; }
    let cancelled = false;
    (async () => {
      const { data, error: fetchError } = await getPitchVideo(pitchId);
      if (cancelled || fetchError || !data?.data) { return; }
      setAttachment({
        id: data.data.id,
        posterUrl: data.data.attributes.poster_url,
        videoUrl: data.data.attributes.video_url,
      });
      setPhase('attached');
    })();
    return () => { cancelled = true; };
  }, [pitchId]);

  const setUploading = (uploading) => {
    onUploadingChange?.(uploading);
  };

  const handleRecorded = async (blob) => {
    setShowRecorder(false);
    setError(null);
    const url = URL.createObjectURL(blob);
    localUrlRef.current = url;
    setLocalUrl(url);
    setPhase('uploading');
    setUploading(true);
    try {
      const info = await uploadVideoToCloudinary(blob, {
        sign: signPitchVideoUpload,
        onProgress: (fraction) => setProgress(fraction),
      });
      const ext = (blob.type || '').includes('mp4') ? 'mp4' : 'webm';
      const { data, error: attachError } = await attachPitchVideo(pitchId, {
        ...info, filename: `pitch-video.${ext}`, recorded: true,
      });
      if (attachError || !data?.data) {
        // 404 after all retries = the V1→V2 pitch sync hasn't landed; the raw AR detail
        // ("Couldn't find Interact::Pitch…") must never reach a promoter.
        const message = attachError?.status === 404
          ? "We couldn't attach your video yet — send your pitch, then add the video by editing it."
          : attachError?.body?.errors?.[0]?.detail || 'Could not attach the video to your pitch';
        throw new Error(message);
      }
      setAttachment({
        id: data.data.id,
        posterUrl: data.data.attributes.poster_url,
        videoUrl: data.data.attributes.video_url,
      });
      setPhase('attached');
    } catch (e) {
      setError(e.message || 'Video upload failed');
      setPhase('empty');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDeleteConfirmed = async () => {
    setShowDeleteConfirm(false);
    const { error: deleteError } = await deletePitchVideo(attachment.id);
    if (deleteError) {
      setError(deleteError?.body?.errors?.[0]?.detail || 'Could not delete the video — please try again');
      return;
    }
    if (localUrlRef.current) {
      URL.revokeObjectURL(localUrlRef.current);
      localUrlRef.current = null;
    }
    setLocalUrl(null);
    setAttachment(null);
    setError(null);
    setPhase('empty');
  };

  const sidePanel = opportunityDetails && (
    <div>
      <h6 className="fw-bold mb-2">Opportunity Details</h6>
      {opportunityDetails.title && <p className="fw-semibold mb-2">{opportunityDetails.title}</p>}
      <div className="small text-secondary" style={{ whiteSpace: 'pre-wrap' }}>
        {opportunityDetails.details}
      </div>
    </div>
  );

  return (
    <div className="pitch-video-section mb-3" data-testid="pitch-video-section">
      {phase === 'empty' && (
        <div className="d-flex align-items-center justify-content-between gap-3">
          <span className="pitch-video-prompt">
            Stand out by adding a video pitch! <span className="pitch-video-optional">(optional)</span>
          </span>
          <button
            type="button"
            className="btn pitch-video-record-btn d-flex align-items-center"
            onClick={() => setShowRecorder(true)}
            disabled={!pitchId}
            data-testid="record-video-pitch"
          >
            <i className="fa-solid fa-video" aria-hidden="true" /> Record Video
          </button>
        </div>
      )}

      {phase === 'uploading' && (
        <div className="d-flex align-items-center gap-3" data-testid="video-uploading">
          <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          <span>Uploading your video… {Math.round(progress * 100)}%</span>
        </div>
      )}

      {phase === 'attached' && attachment && (
        <div className="d-flex justify-content-center">
          <div className="position-relative" style={{ width: 260 }} data-testid="video-attached">
            <button
              type="button"
              className="border-0 p-0 bg-transparent w-100"
              onClick={() => setShowPreview(true)}
              aria-label="Play your video pitch"
            >
              {attachment.posterUrl && !posterFailed ? (
                <img
                  src={attachment.posterUrl}
                  alt="Video pitch preview"
                  className="w-100 rounded"
                  onError={() => setPosterFailed(true)}
                />
              ) : (
                <div className="w-100 rounded bg-dark" style={{ aspectRatio: '16/9' }} />
              )}
              <span
                className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center rounded-circle bg-white"
                style={{ width: 48, height: 48, opacity: 0.9 }}
              >
                <i className="fa-solid fa-play" aria-hidden="true" />
              </span>
            </button>
            <button
              type="button"
              className="btn btn-link p-1 position-absolute top-0 end-0 m-1 text-white pitch-video-delete"
              onClick={() => setShowDeleteConfirm(true)}
              aria-label="Delete your video pitch"
              data-testid="delete-video-pitch"
            >
              <i className="fa-light fa-trash" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {error && <div className="text-danger small mt-2" data-testid="video-error">{error}</div>}

      <VideoRecorderModal
        show={showRecorder}
        onHide={() => setShowRecorder(false)}
        onRecorded={handleRecorded}
        sidePanel={sidePanel}
        modalClassName="pitch-video-modal"
      />
      <VideoPreviewModal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        src={localUrl || attachment?.videoUrl}
        title="Your Video Pitch"
        modalClassName="pitch-video-modal"
      />
      <ConfirmVideoDeleteModal
        show={showDeleteConfirm}
        recorded
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirmed}
        modalClassName="pitch-video-modal"
      />
    </div>
  );
}

PitchVideoSection.propTypes = {
  pitchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  opportunityDetails: PropTypes.shape({
    title: PropTypes.string,
    details: PropTypes.string,
  }),
  onUploadingChange: PropTypes.func,
  onAttachedChange: PropTypes.func,
};

PitchVideoSection.defaultProps = {
  pitchId: undefined,
  opportunityDetails: null,
  onUploadingChange: undefined,
  onAttachedChange: undefined,
};

export default PitchVideoSection;
